import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Box,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Fade,
    Alert,
    MenuItem,
    Fab,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import StarIcon from '@mui/icons-material/Star';
import {
    careersImage02,
} from '../../assets/images';
import useParallaxEffect from "../common/useParallaxEffect";
import ScrollToTop from "../common/ScrollToTop";
import { apiService } from '../../services/api';

// Mock data for job listings (replace with actual API call in production)
const jobListings = [
    {
        id: 1,
        title: "Board Certified Behavioral Analyst",
        department: "Clinical",
        location: "Austin, TX",
        type: "Part-time",
        description: "We are seeking a qualified Behavioral Analyst to join our team...",
    },
    {
        id: 2,
        title: "Registered Behavior Technician",
        department: "Clinical",
        location: "Austin, TX",
        type: "Part-time",
        description: "Experienced Registered Behavior Technician needed for our growing practice...",
    },
    {
        id: 3,
        title: "Behavior Technician",
        department: "Clinical",
        location: "Austin, TX",
        type: "Part-time",
        description: "Experienced Behavior Technician needed for our growing practice...",
    }
    // Add more job listings as needed
];


const Careers = () => {
    useParallaxEffect();
    const [submissionStatus, setSubmissionStatus] = useState('idle'); // 'idle', 'submitting', 'success', 'error'

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    const [openJobId, setOpenJobId] = useState(null);
    const [applicationData, setApplicationData] = useState({
        fullName: '',
        email: '',
        phone: '',
        position: '',
        source: '',
        resume: null,
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleApplyClick = (jobId) => {
        setOpenJobId(jobId);
    };

    const handleCloseDialog = () => {
        setOpenJobId(null);
        setApplicationData({
            fullName: '',
            email: '',
            phone: '',
            position: '',
            source: '',
            resume: null,
        });
        setSubmissionStatus('idle');
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setApplicationData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

        if (file.size > maxSize) {
            setSnackbar({
                open: true,
                message: 'File size exceeds 5MB limit.',
                severity: 'error',
            });
            return;
        }

        if (!allowedTypes.includes(file.type)) {
            setSnackbar({
                open: true,
                message: 'Invalid file type. Please upload a PDF or Word document.',
                severity: 'error',
            });
            return;
        }

        setApplicationData(prev => ({ ...prev, resume: file }));
    };

    const handleSubmitApplication = async () => {
        const requiredFields = ['fullName', 'email', 'phone', 'position', 'source'];
        const missingFields = requiredFields.filter(field => !applicationData[field]);

        if (missingFields.length > 0 || !applicationData.resume) {
            let errorMessage = missingFields.length > 0
                ? `Please fill in the following fields: ${missingFields.join(', ')}`
                : 'Please upload a resume';

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
            return;
        }

        if (applicationData.resume && applicationData.resume.size > 5 * 1024 * 1024) {
            setSnackbar({
                open: true,
                message: 'Resume file size exceeds 5MB limit.',
                severity: 'error',
            });
            return;
        }

        setSubmissionStatus('submitting');
        setIsSubmitting(true);

        const formData = new FormData();
        Object.keys(applicationData).forEach(key => {
            if (key === 'resume') {
                if (applicationData.resume) {
                    formData.append('resume', applicationData.resume, applicationData.resume.name);
                }
            } else {
                formData.append(key, applicationData[key]);
            }
        });

        try {
            await apiService.submitJobApplication(formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setSubmissionStatus('success');
            setSnackbar({
                open: true,
                message: 'Application submitted successfully!',
                severity: 'success',
            });

            setApplicationData({
                fullName: '',
                email: '',
                phone: '',
                position: '',
                source: '',
                resume: null,
            });
        } catch (error) {
            console.error('Error submitting application:', error);
            console.error('Error response:', error.response);
            setSubmissionStatus('error');

            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to submit application. Please try again.',
                severity: 'error',
            });
        } finally {
            setIsSubmitting(false);
            handleCloseDialog();
            setTimeout(() => setSubmissionStatus('idle'), 3000);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <>
            <div id="back-to-top-anchor"/>
            <Box>
                <Box sx={{
                    bgcolor: '#f5f5f5',
                    pt: '64px',  // padding-top: 64px
                    pb: '30px',  // padding-bottom: 30px
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <Container maxWidth="lg">
                        <Typography
                            variant="h3"
                            component="h3"
                            align="center"
                            sx={{
                                color: 'textSecondary',
                                fontWeight: 500
                            }}
                        >
                            Join our team and make a difference...
                        </Typography>
                    </Container>
                </Box>

                <Container maxWidth="lg" sx={{mt: 6, mb: 6}}>
                    <Fade in={true} timeout={2000}>
                        <Box sx={{
                            width: '100%',
                            mx: 'auto',
                            mb: 6,
                            display: 'flex',
                            flexDirection: {xs: 'column', sm: 'row'},
                            justifyContent: 'center',
                            alignItems: 'stretch',
                            gap: 2
                        }}>
                            {jobListings.map((job) => (
                                <Box key={job.id} sx={{
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <Card sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        p: 2,
                                        boxShadow: 3,
                                        '&:hover': {
                                            boxShadow: 6,
                                            transform: 'translateY(-5px)',
                                            transition: 'all 0.3s ease-in-out'
                                        }
                                    }}>
                                        <CardContent>
                                            <Typography variant="h6" component="div" gutterBottom>
                                                {job.title}
                                            </Typography>
                                            <Typography color="text.secondary" gutterBottom>
                                                {job.department}
                                            </Typography>
                                            <Box sx={{display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap'}}>
                                                <Chip label={job.location} size="small"/>
                                                <Chip label={job.type} size="small" color="primary"/>
                                            </Box>
                                            <Typography variant="body2" sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                            }}>
                                                {job.description}
                                            </Typography>
                                        </CardContent>
                                        <CardActions>
                                            <Button size="small" onClick={() => handleApplyClick(job.id)}>Apply
                                                Now</Button>
                                        </CardActions>
                                    </Card>
                                </Box>
                            ))}
                        </Box>
                    </Fade>

                    <Box sx={{mt: 8, mb: 8, bgcolor: '#f8f9fa', borderRadius: 4, overflow: 'hidden'}}>
                        <Container maxWidth="lg">
                            <Typography variant="h3" gutterBottom component="h2" align="center"
                                        sx={{mt: 4, mb: 4, fontWeight: 700, color: '#1A365D'}}>
                                Why Choose Pivotal Behavioral Services?
                            </Typography>
                            <Grid container spacing={4} alignItems="center">
                                <Grid item xs={12} md={6}>
                                    <Box sx={{p: 3}}>
                                        <Typography variant="h5" gutterBottom
                                                    sx={{fontWeight: 600, color: '#2F6F62', mb: 3}}>
                                            Empowering Lives
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {[
                                                {
                                                    title: "Competitive Compensation",
                                                    description: "Enjoy a compensation package that recognizes your skills and dedication."
                                                },
                                                {
                                                    title: "Continuous Learning",
                                                    description: "Benefit from paid training and ongoing professional development opportunities."
                                                },
                                                {
                                                    title: "Supportive Environment",
                                                    description: "Thrive in a collaborative team that values your contributions and well-being."
                                                },
                                                {
                                                    title: "Career Advancement",
                                                    description: "Grow within our organization with clear pathways for progression."
                                                },
                                                {
                                                    title: "Fieldwork Supervision",
                                                    description: "Receive free fieldwork supervision hours for being in an ABA master's program."
                                                },
                                                {
                                                    title: "Work-Life Balance",
                                                    description: "Flexible schedules that respect your personal time and commitments."
                                                }
                                            ].map((item, index) => (
                                                <Grid item xs={12} key={index}>
                                                    <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                                                        <Box sx={{mr: 2, color: '#C7AE6A'}}>
                                                            <StarIcon/>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="subtitle1"
                                                                        sx={{fontWeight: 600, color: '#1A365D'}}>
                                                                {item.title}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{color: '#4A5568'}}>
                                                                {item.description}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4}}>
                                        <Box
                                            component="img"
                                            src={careersImage02}
                                            alt="Our work environment"
                                            sx={{
                                                width: '100%',
                                                maxWidth: '300px',
                                                height: 'auto',
                                                borderRadius: 2,
                                                boxShadow: 3,
                                                transition: 'transform 0.3s ease-in-out',
                                                '&:hover': {
                                                    transform: 'scale(1.02)',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Box sx={{textAlign: 'center', mt: 4, mb: 4}}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    component={RouterLink}
                                    to="/contact"
                                    sx={{
                                        bgcolor: '#C09C2C',
                                        color: 'white',
                                        '&:hover': {
                                            bgcolor: '#C7AE6A',
                                        },
                                    }}
                                >
                                    Join Our Team Today
                                </Button>
                            </Box>
                        </Container>
                    </Box>

                    <Dialog open={openJobId !== null} onClose={handleCloseDialog}>
                        <DialogTitle>Apply for Position</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                name="fullName"
                                label="Full Name"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={applicationData.fullName}
                                onChange={handleInputChange}
                                required
                            />
                            <TextField
                                margin="dense"
                                name="email"
                                label="Email Address"
                                type="email"
                                fullWidth
                                variant="outlined"
                                value={applicationData.email}
                                onChange={handleInputChange}
                                required
                            />
                            <TextField
                                margin="dense"
                                name="phone"
                                label="Phone Number"
                                type="tel"
                                fullWidth
                                variant="outlined"
                                value={applicationData.phone}
                                onChange={handleInputChange}
                                required
                            />
                            <TextField
                                select
                                margin="dense"
                                name="position"
                                label="Position"
                                fullWidth
                                variant="outlined"
                                value={applicationData.position}
                                onChange={handleInputChange}
                                required
                            >
                                {jobListings.map((job) => (
                                    <MenuItem key={job.id} value={job.title}>
                                        {job.title}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                margin="dense"
                                name="source"
                                label="How did you hear about us?"
                                fullWidth
                                variant="outlined"
                                value={applicationData.source}
                                onChange={handleInputChange}
                                required
                            >
                                {['Company Website', 'Job Board', 'Social Media', 'Referral', 'Other'].map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Box sx={{mt: 2}}>
                                <input
                                    accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    style={{display: 'none'}}
                                    id="resume-file"
                                    type="file"
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="resume-file">
                                    <Button variant="outlined" component="span">
                                        Upload Resume
                                    </Button>
                                </label>
                                {applicationData.resume && (
                                    <Typography variant="body2" sx={{mt: 1}}>
                                        File selected: {applicationData.resume.name}
                                    </Typography>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Cancel</Button>
                            <Button
                                onClick={handleSubmitApplication}
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting || submissionStatus === 'success'}
                            >
                                {submissionStatus === 'submitting' ? 'Submitting...' :
                                    submissionStatus === 'success' ? 'Submitted!' :
                                        submissionStatus === 'error' ? 'Try Again' : 'Submit Application'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Fab
                        color="primary"
                        size="small"
                        aria-label="scroll back to top"
                        sx={{
                            position: 'fixed',
                            bottom: 16,
                            right: 16,
                            display: 'none',
                        }}
                    >
                    </Fab>
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbar.severity}
                            sx={{ width: '100%' }}
                            variant="filled"
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Container>
            </Box>
            <ScrollToTop />
        </>
    );
};

export default Careers;